import { useEffect } from "react";

const SITE_NAME = "Summer Jobs in Greece";
const SITE_URL = "https://www.summerjobsingreeceguide.com";
const DEFAULT_DESCRIPTION =
  "Summer Jobs in Greece features seasonal job listings, blog guides, and editorial travel content for island life.";

type SeoProps = {
  title?: string;
  description?: string;
  path?: string;
  canonicalUrl?: string;
  image?: string;
  type?: "website" | "article";
  robots?: string;
};

export function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  canonicalUrl,
  image,
  type = "website",
  robots = "index,follow",
}: SeoProps) {
  useEffect(() => {
    const resolvedTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const resolvedCanonicalUrl = canonicalUrl ?? buildAbsoluteUrl(path);

    document.title = resolvedTitle;

    setMetaByName("description", description);
    setMetaByName("robots", robots);
    setMetaByProperty("og:site_name", SITE_NAME);
    setMetaByProperty("og:title", resolvedTitle);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:type", type);
    setMetaByProperty("og:url", resolvedCanonicalUrl);
    setMetaByName("twitter:card", image ? "summary_large_image" : "summary");
    setMetaByName("twitter:title", resolvedTitle);
    setMetaByName("twitter:description", description);

    if (image) {
      setMetaByProperty("og:image", image);
      setMetaByName("twitter:image", image);
    } else {
      removeManagedTag('meta[property="og:image"]');
      removeManagedTag('meta[name="twitter:image"]');
    }

    setCanonicalLink(resolvedCanonicalUrl);

    return () => {
      removeManagedTag('meta[name="robots"]');
      removeManagedTag('meta[property="og:site_name"]');
      removeManagedTag('meta[property="og:title"]');
      removeManagedTag('meta[property="og:description"]');
      removeManagedTag('meta[property="og:type"]');
      removeManagedTag('meta[property="og:url"]');
      removeManagedTag('meta[property="og:image"]');
      removeManagedTag('meta[name="twitter:card"]');
      removeManagedTag('meta[name="twitter:title"]');
      removeManagedTag('meta[name="twitter:description"]');
      removeManagedTag('meta[name="twitter:image"]');
      removeManagedTag('link[rel="canonical"]');
    };
  }, [canonicalUrl, description, image, path, robots, title, type]);

  return null;
}

function buildAbsoluteUrl(path?: string) {
  if (!path) return SITE_URL;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${normalizedPath}`;
}

function setMetaByName(name: string, content: string) {
  setMetaTag(`meta[name="${name}"]`, (meta) => meta.setAttribute("name", name), content);
}

function setMetaByProperty(property: string, content: string) {
  setMetaTag(
    `meta[property="${property}"]`,
    (meta) => meta.setAttribute("property", property),
    content
  );
}

function setMetaTag(
  selector: string,
  init: (element: HTMLMetaElement) => void,
  content: string
) {
  let meta = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    init(meta);
    meta.setAttribute("data-managed-seo", "true");
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
}

function setCanonicalLink(href: string) {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    link.setAttribute("data-managed-seo", "true");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

function removeManagedTag(selector: string) {
  const element = document.head.querySelector(`${selector}[data-managed-seo="true"]`);
  element?.parentElement?.removeChild(element);
}
